/**
 * Realtime singleton — one WebSocket per app.
 *
 * Channel taxonomy mirrors the API ``services.realtime`` module:
 *
 *   user:{id}:{balance|trade|wallet|notify}
 *   global:{price|kill_switch}
 *
 * Every server frame is the same JSON envelope:
 *
 *   { channel, type, ts, payload }
 *
 * Heartbeat: we send ``{type:'ping'}`` every 25s and expect a
 * ``{type:'pong'}`` back. If the socket closes for any reason we
 * reconnect with capped exponential backoff (1s, 2s, 4s, … 30s).
 *
 * Mock mode: this module reads ``isMockEnabled()`` and degrades to a
 * no-op transport so the webapp boots in a regular browser without a
 * backend. ``emitMockEvent()`` lets fixtures/tests inject events.
 */

import { getToken } from './api';
import { isMockEnabled } from './mock';

export type RealtimeEnvelope<P = unknown> = {
  channel: string;
  type: string;
  ts: number;
  payload: P;
};

type Handler = (env: RealtimeEnvelope) => void;
type Matcher = string | RegExp | ((channel: string) => boolean);

interface Subscription {
  matcher: Matcher;
  handler: Handler;
}

const PING_INTERVAL_MS = 25_000;
const RECONNECT_MIN_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

let socket: WebSocket | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = RECONNECT_MIN_MS;
let started = false;
const subscriptions = new Set<Subscription>();

function matches(matcher: Matcher, channel: string): boolean {
  if (typeof matcher === 'string') {
    if (matcher.endsWith(':*')) {
      const prefix = matcher.slice(0, -1);
      return channel.startsWith(prefix);
    }
    return channel === matcher;
  }
  if (matcher instanceof RegExp) return matcher.test(channel);
  return matcher(channel);
}

function dispatch(env: RealtimeEnvelope): void {
  for (const sub of subscriptions) {
    if (matches(sub.matcher, env.channel)) {
      try {
        sub.handler(env);
      } catch (e) {
        // A misbehaving subscriber must not stop the others — mirror
        // the way Redis pubsub treats subscriber failures (just log).
        // eslint-disable-next-line no-console
        console.error('[realtime] handler threw', e);
      }
    }
  }
}

function clearTimers() {
  if (pingTimer !== null) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect() {
  if (!started) return;
  if (reconnectTimer !== null) return;
  const delay = reconnectDelay;
  reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    openSocket();
  }, delay);
}

function openSocket() {
  if (typeof window === 'undefined') return;
  const token = getToken();
  if (!token) {
    // No token yet — try again shortly. Auth runs on app boot, so
    // the first attempt typically races; we just wait it out.
    scheduleReconnect();
    return;
  }
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const url = `${proto}://${window.location.host}/api/v1/ws?token=${encodeURIComponent(token)}`;
  let ws: WebSocket;
  try {
    ws = new WebSocket(url);
  } catch {
    scheduleReconnect();
    return;
  }
  socket = ws;

  ws.onopen = () => {
    reconnectDelay = RECONNECT_MIN_MS;
    if (pingTimer === null) {
      pingTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, PING_INTERVAL_MS);
    }
  };

  ws.onmessage = (e) => {
    const data = typeof e.data === 'string' ? e.data : '';
    if (!data) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== 'object') return;
    const obj = parsed as Record<string, unknown>;
    if (obj.type === 'ping' || obj.type === 'pong') return;
    if (typeof obj.channel !== 'string' || typeof obj.type !== 'string') return;
    dispatch(obj as unknown as RealtimeEnvelope);
  };

  ws.onclose = () => {
    if (pingTimer !== null) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
    if (socket === ws) socket = null;
    scheduleReconnect();
  };

  ws.onerror = () => {
    // Let onclose drive reconnect — error fires before close in most
    // browsers, and re-running the backoff twice would halve the wait.
  };
}

export function startRealtime(): void {
  if (started) return;
  started = true;
  if (isMockEnabled()) return; // mock mode — no real socket
  openSocket();
}

export function stopRealtime(): void {
  started = false;
  clearTimers();
  if (socket) {
    try {
      socket.close();
    } catch {
      /* ignore */
    }
    socket = null;
  }
}

export function subscribeRealtime(
  matcher: Matcher,
  handler: Handler,
): () => void {
  const sub: Subscription = { matcher, handler };
  subscriptions.add(sub);
  return () => {
    subscriptions.delete(sub);
  };
}

/** Inject an envelope as if it came from the server. Mock-mode + tests. */
export function emitMockEvent(env: RealtimeEnvelope): void {
  dispatch(env);
}

/** Test/dev helper — drop every subscription and tear down the socket. */
export function __resetRealtime(): void {
  subscriptions.clear();
  stopRealtime();
  reconnectDelay = RECONNECT_MIN_MS;
}
